import { sentry } from 'common/helpers'


const isServer = process.env.WEBPACK === 'build.server'

const ffErrors = [
  'NS_ERROR_FAILURE', // has max size
  'NS_ERROR_FILE_CORRUPTED', // is crashed
  'NS_ERROR_FILE_NO_DEVICE_SPACE',
]

class LocalStorage {

  get(name: string, defaultValue: any, storage: any) {
    if (isServer) {
      console.error('DON\'T USE localStorage ON SERVER SIDE')
      return defaultValue
    }

    if (!storage) {
      console.error('storage is null')
      return defaultValue
    }

    try {
      const value = JSON.parse(storage.getItem(name))

      return value !== null ? value : defaultValue
    }
    catch (err) {
      if (ffErrors.includes(err.name)) {
        this.clear(storage)

        return defaultValue
      }

      console.error(`localStorage error: name - ${name}`, err)
      sentry.captureException('Local or session storage can\'t parse value', {
        extra: {
          name,
          err,
        },
      })

      this.remove(name, storage) // remove invalid data

      return defaultValue
    }
  }

  set(name: string, value: any, storage: any) {
    if (isServer) {
      console.error('DON\'T USE localStorage ON SERVER SIDE')
      return
    }

    if (!storage) {
      console.error('storage is null')
      return
    }

    try {
      storage.setItem(name, JSON.stringify(value))
    }
    catch (err) {
      console.log(name, value)
      console.error('localStorage error', err)

      if (ffErrors.includes(err.name)) {
        this.clear(storage)
      }
    }
  }

  remove(name: string, storage: any) {
    if (isServer) {
      console.error('DON\'T USE localStorage ON SERVER SIDE')
      return
    }

    if (!storage) {
      console.error('storage is null')
      return
    }

    try {
      storage.removeItem(name)
    }
    catch (err) {
      console.log(name)
      console.error('localStorage error', err)
    }
  }

  // to show error alert only once
  corruptedAlertWasShowed = false

  clear(storage: any) {
    // Firefox corrupted file error, try to clean up the storage, and ignore error
    // https://stackoverflow.com/questions/18877643/error-in-local-storage-ns-error-file-corrupted-firefox
    try {
      console.log(`clean up storage`)

      storage.clear()
    }
    catch (error) {
      if (ffErrors.includes(error.name) && !this.corruptedAlertWasShowed) {
        alert('Sorry, it looks like your browser storage has been corrupted. '
          + 'Please clear your storage by going to Tools -> Clear Recent History -> Cookies and set time range to \'Everything\'. '
          + 'This will remove the corrupted browser storage across all sites.'
        )

        this.corruptedAlertWasShowed = true
      }

      console.error(error)
    }
  }

  getItem<T>(name: string, defaultValue?: any): T {
    return this.get(name, defaultValue, localStorage)
  }

  setItem(name: string, value: any) {
    this.set(name, value, localStorage)
  }

  removeItem(name: string) {
    this.remove(name, localStorage)
  }

  getSessionItem<T>(name: string, defaultValue?: any): T {
    return this.get(name, defaultValue, sessionStorage)
  }

  setSessionItem(name: string, value: any) {
    this.set(name, value, sessionStorage)
  }

  removeSessionItem(name: string) {
    this.remove(name, sessionStorage)
  }
}


export default new LocalStorage()
